import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { StripeProvider } from "@/components/payment/StripeProvider";
import { AuthModalsProvider } from "@/contexts/AuthModalsContext";
import { SearchModalProvider } from "@/contexts/SearchModalContext";
import { AuthProvider } from "@/auth/AuthProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { BottomNav } from "@/components/BottomNav";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/ui/skip-to-content";
import { ContentFallback } from "@/components/ui/content-fallback";
import { useAuth } from "@/auth/AuthProvider";
import Protected from "./auth/Protected";

// Lazy load route components
const Index = lazy(() => import("./pages/Index"));
const Stays = lazy(() => import("./pages/Stays"));
const HotelDetailsLuxury = lazy(() => import("./pages/HotelDetailsLuxury"));
const Reviews = lazy(() => import("./pages/Reviews"));
const DatabaseTest = lazy(() => import("./pages/DatabaseTest"));
const DumpProcessor = lazy(() => import("./pages/DumpProcessor"));
const Membership = lazy(() => import("./pages/Membership"));
const RequestAccess = lazy(() => import("./pages/RequestAccess"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AccountLayout = lazy(() => import("./pages/account/AccountLayout"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Admin = lazy(() => import("./pages/Admin"));
const DataRestoration = lazy(() => import("./pages/DataRestoration"));

const queryClient = new QueryClient();

const AppContent = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { user } = useAuth();

  // Three-tier visibility logic for BottomNav
  const shouldShowBottomNav = () => {
    // Tier 1: User must be authenticated
    if (!user) return false;

    // Tier 2: Route whitelist - ONLY these routes can show nav
    const allowedRoutes = [
      /^\/stays/, // Search results page
      /^\/account/, // Account pages (all tabs)
    ];

    const isAllowedRoute = allowedRoutes.some((r) => r.test(location.pathname));
    if (!isAllowedRoute) return false;

    // Tier 3: Explicitly blocked routes (even if whitelisted pattern matches)
    const blockedRoutes = [
      /^\/hotel\/[^/]+$/,
      /^\/hotel-details/,
      /^\/checkout$/,
      /^\/booking-confirmation$/,
    ];

    const isBlocked = blockedRoutes.some((r) => r.test(location.pathname));
    if (isBlocked) return false;

    return true;
  };

  const showNav = shouldShowBottomNav();

  return (
    <div className={`app-root ${showNav ? 'pb-20 md:pb-0' : ''}`}>
      {children}
      {showNav && <BottomNav />}
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <StripeProvider>
          <AuthProvider>
            <AuthModalsProvider>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <SearchModalProvider>
                  <SkipToContent />
                  <ScrollToTop />
                  <Toaster />
                  <Sonner />
                  <AppContent>
                    <ErrorBoundary>
                      <Routes>
                        <Route path="/" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Index />
                          </Suspense>
                        } />
                        
                        <Route path="/stays" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ErrorBoundary><Stays /></ErrorBoundary>
                          </Suspense>
                        } />
                        
                        <Route path="/membership" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Membership />
                          </Suspense>
                        } />
                        
                        <Route path="/hotel/:hotelId" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ErrorBoundary><HotelDetailsLuxury /></ErrorBoundary>
                          </Suspense>
                        } />
                        
                        <Route path="/hotel-details" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ErrorBoundary><HotelDetailsLuxury /></ErrorBoundary>
                          </Suspense>
                        } />
                        
                        <Route path="/checkout" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ErrorBoundary><Checkout /></ErrorBoundary>
                          </Suspense>
                        } />
                        
                        <Route path="/booking-confirmation" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <BookingConfirmation />
                          </Suspense>
                        } />
                        
                        <Route path="/request-access" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <RequestAccess />
                          </Suspense>
                        } />
                        
                        <Route path="/forgot-password" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ForgotPassword />
                          </Suspense>
                        } />
                        
                        <Route path="/reset-password" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ResetPassword />
                          </Suspense>
                        } />
                        
                        <Route path="/reviews" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Reviews />
                          </Suspense>
                        } />
                        
                        <Route path="/admin" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Protected><Admin /></Protected>
                          </Suspense>
                        } />
                        
                        <Route path="/dump-processor" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Protected><DumpProcessor /></Protected>
                          </Suspense>
                        } />
                        
                        <Route path="/data-restoration" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <DataRestoration />
                          </Suspense>
                        } />
                        
                        <Route path="/account" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Protected><ErrorBoundary><AccountLayout /></ErrorBoundary></Protected>
                          </Suspense>
                        } />
                        
                        <Route path="/database-test" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <DatabaseTest />
                          </Suspense>
                        } />
                        
                        <Route path="*" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <NotFound />
                          </Suspense>
                        } />
                      </Routes>
                    </ErrorBoundary>
                  </AppContent>
                </SearchModalProvider>
              </BrowserRouter>
            </AuthModalsProvider>
          </AuthProvider>
        </StripeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
